import { deserialize, DocumentObject, serialize } from '@jsonapiworld/jsonapi-serializer';
import { backendApiGateway } from '~/client/backend';
import { MetaDataCursorPage, pagedMetaSchema } from '~/types/jsonapi';
import { isDefined } from '~/types/typeGuards';
import { UpdateVATBookParameters, VATBook, VATBookFilters } from '~/types/vat-bookkeeping';
import { buildJsonAPIQueryParams, jsonAPICursorPaginatorBuilder, jsonAPIFilterBuilder } from '~/utils/jsonapi-query';

const vatBooksPageSize = 50;

export const fetchAllVATBooks = async (companyId: string, filters: VATBookFilters = {}): Promise<VATBook[]> => {
  let allBooks: VATBook[] = [];
  let fetchedBooks: VATBook[] = [];

  let cursor: MetaDataCursorPage['cursor'] | null = null;
  const filterBuilder = jsonAPIFilterBuilder();
  filterBuilder.eq('company.id', companyId);
  if (isDefined(filters.bookType)) filterBuilder.eq('bookType', filters.bookType);
  if (isDefined(filters.year)) filterBuilder.eq('year', filters.year);

  do {
    const pageBuilder = jsonAPICursorPaginatorBuilder({ size: vatBooksPageSize });
    if (cursor) pageBuilder.cursor(cursor);
    const searchParams = buildJsonAPIQueryParams(filterBuilder, pageBuilder);

    const { data: results } = await backendApiGateway.get<DocumentObject & { meta: { page: MetaDataCursorPage } }>(
      '/api/v1/vat-books',
      { params: searchParams },
    );

    fetchedBooks = deserialize<VATBook>(results) as VATBook[];
    allBooks = allBooks.concat(fetchedBooks);
    const { page } = pagedMetaSchema.parse(results.meta);
    cursor = page.cursor;
  } while (fetchedBooks.length === vatBooksPageSize);

  return allBooks;
};

export const updateVATBook = async ({ id, ...attributes }: UpdateVATBookParameters): Promise<VATBook> => {
  const payload = serialize({ id, ...attributes }, 'vat-book');

  const { data } = await backendApiGateway.patch<DocumentObject>(`/api/v1/vat-books/${id}`, payload);
  return deserialize<VATBook>(data) as VATBook;
};

export const deleteVATBook = async (id: VATBook['id']): Promise<void> => {
  await backendApiGateway.delete(`/api/v1/vat-books/${id}`);
};
